import * as THREE from 'three'

// ============================================================================
//  tileLibrary — katalog nawierzchni z płytek (tiles.json).
//
//  Model danych: `palettes` (zakresy barw id → swatch), `models` (geometria
//  płytki, wskazanie zakresu `palette`, `defaultColors`, `baseTexture` faktury),
//  `default` (startowy model i wymiary). Kolory startowe = defaultColors modelu.
//
//  Barwienie: jedna NEUTRALNA (szara) tekstura per model jest barwiona w silniku
//  przez material.color = swatch. map × color daje kolorową płytkę z fakturą.
//  Ścieżki w JSON są względne do katalogu publicznego; prefiksujemy BASE_URL Vite.
// ============================================================================

const BASE = import.meta.env.BASE_URL || '/'
const grayCache = new Map()   // modelId → { map, bump }

// Wczytaj katalog płytek (JSON).
export async function loadCatalog() {
  const res = await fetch(`${BASE}tiles/tiles.json`)
  if (!res.ok) throw new Error(`Nie można wczytać tiles.json (HTTP ${res.status})`)
  return res.json()
}

// Model po id (z fallbackiem do pierwszego).
export function resolveModel(catalog, id) {
  return catalog.models.find((m) => m.id === id) || catalog.models[0]
}

// Paleta (zakres barw) modelu — tablica {id,name,swatch}. Model wskazuje zakres
// kluczem `palette` w catalog.palettes; fallback do pierwszego zakresu, gdy klucz
// nieznany (odporność na literówki w danych).
export function resolveModelPalette(catalog, modelId) {
  const model = resolveModel(catalog, modelId)
  return catalog.palettes[model.palette] || Object.values(catalog.palettes)[0]
}

// Swatch (hex) koloru z PODANEJ palety (z neutralnym fallbackiem). Wołający
// dostarcza paletę bieżącego modelu (resolveModelPalette).
export function resolveColor(palette, id) {
  const entry = palette.find((p) => p.id === id)
  return entry ? entry.swatch : '#888888'
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Nie można wczytać obrazu: ${src}`))
    img.src = src
  })
}

// Szara tekstura faktury do barwienia (mnożona przez swatch w materiale strefy).
// Cache per model. Krzywa: lico płytki podbite ku bieli (swatch w PEŁNEJ barwie —
// WYSIWYG), spoiny/otwory ciemniejsze (widoczne łączenia płytek).
export async function getGrayTexture(model, maxAnisotropy = 8) {
  if (grayCache.has(model.id)) return grayCache.get(model.id)

  const img = await loadImage(`${BASE}${model.baseTexture}`)
  const w = img.naturalWidth || img.width
  const h = img.naturalHeight || img.height
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0)
  const imgData = ctx.getImageData(0, 0, w, h)
  const px = imgData.data

  // Tekstura jest MNOŻNIKIEM barwy (swatch × tekstura w materiale strefy). Realne
  // tekstury płytek są ciemne (lico ~0.5 luminancji, max ~0.58), więc surowe
  // mnożenie gasiłoby swatch o ~40% → ciemny kort. Dlatego normalizujemy względem
  // jasności LICA: punkt bieli = średnia luminancja (lico to większość pikseli, więc
  // leży powyżej średniej i saturuje do ~1.0 → PEŁNY swatch = WYSIWYG), a tylko
  // ciemniejsze spoiny/otwory schodzą do SEAM_FLOOR (subtelna, widoczna kratka).
  // Samokalibracja per tekstura — działa niezależnie od jasności danego pliku.
  const SEAM_FLOOR = 0.5          // jasność spoin (0 = czarne, 1 = spoiny niewidoczne)
  let minLum = 1
  let sumLum = 0
  const count = px.length / 4
  for (let i = 0; i < px.length; i += 4) {
    const lum = (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) / 255
    if (lum < minLum) minLum = lum
    sumLum += lum
  }
  const whitePoint = sumLum / count
  const span = Math.max(1e-3, whitePoint - minLum)
  for (let i = 0; i < px.length; i += 4) {
    const lum = (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) / 255
    const t = Math.min(1, Math.max(0, (lum - minLum) / span)) // lico → 1, spoiny → 0
    const v = Math.round(255 * (SEAM_FLOOR + (1 - SEAM_FLOOR) * t))
    px[i] = px[i + 1] = px[i + 2] = v
  }
  ctx.putImageData(imgData, 0, 0)

  const map = new THREE.CanvasTexture(canvas)
  map.wrapS = map.wrapT = THREE.RepeatWrapping
  map.colorSpace = THREE.SRGBColorSpace
  map.anisotropy = maxAnisotropy
  map.needsUpdate = true

  const result = { map }
  grayCache.set(model.id, result)
  return result
}
